import { useState } from 'react';

const projectTypes = [
  'Photography Shoot',
  'Video Production',
  'Podcast Recording',
  'Content Creation',
  'Brand Campaign',
  'Event / Workshop',
  'Other',
];

export default function FinalCTA() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    projectType: '',
    date: '',
  });
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    setFormData((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!formData.name || !formData.email || !formData.projectType) {
      setError('Please fill in all required fields.');
      return;
    }
    setError('');
    setLoading(true);
    try {
      const body = new URLSearchParams();
      body.append('name', formData.name);
      body.append('email', formData.email);
      body.append('projectType', formData.projectType);
      body.append('date', formData.date);
      await fetch('https://readdy.ai/api/form/d6ujul6dp0k8rarn7ln0', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: body.toString(),
      });
      setSubmitted(true);
    } catch {
      setError('Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="book" className="py-24 bg-stone-950 relative overflow-hidden">
      {/* Background texture image */}
      <div className="absolute inset-0 opacity-10">
        <img
          src="https://public.readdy.ai/ai/img_res/84e13c07ca8eb76f24b2709e07e53637.jpg"
          alt=""
          className="w-full h-full object-cover object-center"
        />
      </div>
      <div className="absolute inset-0 bg-gradient-to-b from-stone-950/80 via-transparent to-stone-950/80" />

      <div className="relative z-10 max-w-7xl mx-auto px-6">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          {/* Left content */}
          <div>
            <span className="text-amber-400 text-xs font-bold uppercase tracking-widest mb-4 block">
              Get Started Today
            </span>
            <h2 className="font-serif text-4xl md:text-5xl font-bold text-white mb-6 leading-tight">
              Ready to Book Your Studio Space?
            </h2>
            <p className="text-stone-400 text-lg leading-relaxed mb-10">
              Fill out the form and our studio team will contact you within 24 hours to confirm availability and finalize your booking.
            </p>

            {/* Trust signals */}
            <div className="space-y-4">
              {[
                { icon: 'ri-checkbox-circle-fill', text: 'Instant availability confirmation' },
                { icon: 'ri-checkbox-circle-fill', text: 'No hidden fees — transparent pricing' },
                { icon: 'ri-checkbox-circle-fill', text: 'Cancel or reschedule up to 48 hours before' },
                { icon: 'ri-checkbox-circle-fill', text: 'Professional support team on standby' },
              ].map((item) => (
                <div key={item.text} className="flex items-center gap-3">
                  <div className="w-5 h-5 flex items-center justify-center flex-shrink-0">
                    <i className={`${item.icon} text-amber-400 text-lg`} />
                  </div>
                  <span className="text-stone-300 text-sm">{item.text}</span>
                </div>
              ))}
            </div>

            {/* Quick phone CTA */}
            <div className="mt-10 flex items-center gap-4">
              <a
                href="tel:+18005551234"
                className="flex items-center gap-3 bg-stone-800 hover:bg-stone-700 border border-stone-700 text-white px-5 py-3.5 rounded-xl transition-all cursor-pointer"
              >
                <div className="w-9 h-9 flex items-center justify-center bg-amber-500 rounded-lg flex-shrink-0">
                  <i className="ri-phone-fill text-white" />
                </div>
                <div>
                  <p className="text-xs text-stone-400">Prefer to call?</p>
                  <p className="text-sm font-semibold text-white whitespace-nowrap">(800) 555-1234</p>
                </div>
              </a>
            </div>
          </div>

          {/* Right form */}
          <div className="bg-white rounded-3xl p-8 md:p-10">
            {submitted ? (
              <div className="text-center py-10">
                <div className="w-16 h-16 flex items-center justify-center bg-amber-100 rounded-full mx-auto mb-5">
                  <i className="ri-check-double-line text-amber-500 text-3xl" />
                </div>
                <h3 className="font-serif text-2xl font-bold text-stone-900 mb-3">Booking Inquiry Sent!</h3>
                <p className="text-stone-500 text-sm leading-relaxed">
                  Our studio team will reach out within 24 hours to confirm your space and finalize the details.
                </p>
              </div>
            ) : (
              <>
                <h3 className="font-serif text-2xl font-bold text-stone-900 mb-2">Book Your Studio</h3>
                <p className="text-stone-500 text-sm mb-7">Tell us about your project and we&apos;ll find the perfect space.</p>

                {error && (
                  <div className="mb-5 bg-red-50 border border-red-200 text-red-700 text-sm px-4 py-3 rounded-lg">
                    {error}
                  </div>
                )}

                <form
                  onSubmit={handleSubmit}
                  data-readdy-form
                  className="space-y-4"
                >
                  <div>
                    <label className="block text-stone-700 text-xs font-semibold uppercase tracking-wide mb-1.5">
                      Full Name <span className="text-red-400">*</span>
                    </label>
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      placeholder="Your full name"
                      required
                      className="w-full border border-stone-200 text-stone-900 placeholder-stone-400 text-sm px-4 py-3 rounded-lg focus:outline-none focus:border-amber-400 transition-colors"
                    />
                  </div>
                  <div>
                    <label className="block text-stone-700 text-xs font-semibold uppercase tracking-wide mb-1.5">
                      Email Address <span className="text-red-400">*</span>
                    </label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      placeholder="you@email.com"
                      required
                      className="w-full border border-stone-200 text-stone-900 placeholder-stone-400 text-sm px-4 py-3 rounded-lg focus:outline-none focus:border-amber-400 transition-colors"
                    />
                  </div>
                  <div>
                    <label className="block text-stone-700 text-xs font-semibold uppercase tracking-wide mb-1.5">
                      Project Type <span className="text-red-400">*</span>
                    </label>
                    <select
                      name="projectType"
                      value={formData.projectType}
                      onChange={handleChange}
                      required
                      className="w-full border border-stone-200 text-stone-900 text-sm px-4 py-3 rounded-lg focus:outline-none focus:border-amber-400 transition-colors cursor-pointer bg-white appearance-none"
                    >
                      <option value="">Select project type...</option>
                      {projectTypes.map((t) => (
                        <option key={t} value={t}>{t}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-stone-700 text-xs font-semibold uppercase tracking-wide mb-1.5">
                      Preferred Date
                    </label>
                    <input
                      type="date"
                      name="date"
                      value={formData.date}
                      onChange={handleChange}
                      className="w-full border border-stone-200 text-stone-900 text-sm px-4 py-3 rounded-lg focus:outline-none focus:border-amber-400 transition-colors cursor-pointer"
                    />
                  </div>

                  <button
                    type="submit"
                    disabled={loading}
                    className="w-full bg-amber-500 hover:bg-amber-600 disabled:opacity-60 text-white font-semibold py-4 rounded-xl transition-all cursor-pointer whitespace-nowrap text-sm mt-2"
                  >
                    {loading ? 'Sending...' : 'Request Studio Booking'}
                  </button>

                  <p className="text-stone-400 text-xs text-center">
                    By submitting, you agree to our{' '}
                    <a href="#" className="underline cursor-pointer hover:text-stone-600" rel="nofollow">Privacy Policy</a>.
                    No spam, ever.
                  </p>
                </form>
              </>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
